/**
 * Minimal keyboard state tracker used by the World scene instead of Phaser's KeyboardPlugin.
 * It keys on both `event.code` (physical key) and `event.key` (logical key), ignores presses that
 * happen inside text fields, and prevents the browser default for arrows and space so the page
 * (or a hosting app) does not scroll or steal the key.
 */

const down = new Set<string>();
let installed = false;

const PREVENT_DEFAULT = new Set(["arrowup", "arrowdown", "arrowleft", "arrowright", " ", "spacebar"]);

function isTextTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

function names(e: KeyboardEvent): string[] {
  const out: string[] = [];
  if (e.code) out.push(e.code.toLowerCase());
  if (e.key) out.push(e.key.toLowerCase());
  return out;
}

export function installKeyboard(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("keydown", (e) => {
    if (isTextTarget(e.target)) return;
    for (const n of names(e)) down.add(n);
    if (PREVENT_DEFAULT.has(e.key.toLowerCase())) e.preventDefault();
  });
  window.addEventListener("keyup", (e) => {
    for (const n of names(e)) down.delete(n);
  });
  window.addEventListener("blur", () => down.clear());
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) down.clear();
  });
}

export function isDown(...keys: string[]): boolean {
  for (const k of keys) if (down.has(k)) return true;
  return false;
}

export const KEYS = {
  up: ["arrowup", "keyw", "w"],
  down: ["arrowdown", "keys", "s"],
  left: ["arrowleft", "keya", "a"],
  right: ["arrowright", "keyd", "d"],
  action: ["keye", "e", " ", "space", "enter"],
} as const;
